import { AppPlatform, AutomationStatus, AutomationType, RolloutMonitoring } from '../types';
import { RecursiveStringifyEnums } from './buildDistro';
import { isIosPlatform } from '@/helpers';
import { topNewStabilityIssues, topStabilityIssues } from './appHome';

const concernReview = (version: string) => ({
    author: '',
    version,
    rating: '2',
    id: '8551204471',
    title: 'Slower since the update',
    content:
        'The app has been noticeably slower since the latest update — screens take a while to load and checkout sometimes needs a retry. Hope this gets fixed soon, everything else is great.',
    fetchedAt: '2022-04-09T02:56:53.521102766Z',
    submittedAt: '2022-04-08T10:22:37-07:00',
});

export const baseRolloutData: (
    platform: AppPlatform,
    releasedAt?: string,
    internalVersionString?: string,
) => RecursiveStringifyEnums<RolloutMonitoring> = (
    platform,
    releasedAt = undefined,
    internalVersionString = '1.0.0',
) => ({
    platform: platform,
    appStoreType: platform === AppPlatform.Ios ? 'apple' : 'google',
    appStoreIntegrationType: platform === AppPlatform.Ios ? 'apple' : 'google',
    phasedRelease: null,
    releasedAt: releasedAt,
    arePhasedReleaseAutomationsPaused: false,
    internalVersionString: internalVersionString,
    appStoreSelectedBuilds: [
        {
            isRecentBuild: true,
            appStoreBuild:
                isIosPlatform(platform) ?
                    {
                        buildNumber: '297',
                        id: 'fdeaddb9-87af-446b-baff-91eb85d1446c',
                        integrationId: 'apple',
                        isProcessed: true,
                        type: 'ios-binary',
                        uploadedAt: '2022-03-15T12:31:21-07:00',
                    }
                :   {
                        buildNumber: '27500',
                        id: '27500',
                        integrationId: 'google',
                        isProcessed: true,
                        type: 'android-aab',
                        uploadedAt: '2022-03-15T18:11:14.055381Z',
                    },
            associatedCIBuild: {
                commitUrl: '',
                buildHash: 'A5LZuDfhn3e6WNweXBzV8AR1w6c=',
                buildIdentifier: isIosPlatform(platform) ? '297' : '27500',
                integrationId: 'bitrise',
                buildUrl: 'https://app.bitrise.io/build/123',
                buildStartedAt: '2022-03-15T19:17:48Z',
                buildFinishedAt: '2022-03-15T19:28:41Z',
                buildStatus: 'success',
                providerBuildStatusString: 'Success',
                commit: '0fb22bf26bc7c458a518c08d46c162d3045cf329',
                commitMessage:
                    'Merge pull request #119 from demo/development\n\nRunway promoted branch `development` to `main` for release.',
                commitAuthor: null,
            },
            isCurrentlySelected: true,
            isNewestAppStoreBuild: true,
            stabilityInfos: {
                'sentry:xid': {
                    version: internalVersionString,
                    buildIdentifier: '',
                    appStoreAppIdentifier: '',
                    integrationId: 'sentry',
                    sessionStability: 99.95987785034438,
                    userStability: 99.95534195304525,
                    sessionAdoption: 5.29375639713409,
                    userAdoption: 5.80524642289348,
                    adoptionPeriodString: '24-48H',
                    url: 'https://sentry.com',
                    environmentName: 'Production',
                    topNewStabilityIssues: topNewStabilityIssues(platform),
                    topStabilityIssues: topStabilityIssues(platform),
                    topNewStabilityIssuesNonFatal: [],
                    topStabilityIssuesNonFatal: [],
                },
            },
            selectedBuildReason: 'autoRunwaySelection',
        },
    ],
    rolloutStartedAt: null,
    appStoreReviewData: {
        averageRating: 4.51706,
        totalRatingsCount: 586,
        issues: [
            {
                description:
                    'Users report the app is slower to load after the latest update, occasionally requiring a retry during checkout.',
                isNew: true,
                examples: [concernReview(internalVersionString)],
            },
        ],
        recentReviews: [
            concernReview(internalVersionString),
            {
                author: '',
                version: '1.0.0',
                rating: '4',
                id: '8542838247',
                title: 'Love the app!',
                content:
                    "I love the app. It was easy to use. This is one of my favorite apps and I can't wait to see what they add to it next!.",
                fetchedAt: '2022-04-09T02:56:53.521102766Z',
                submittedAt: '2022-04-07T14:51:08-07:00',
            },
            {
                author: '',
                version: '1.0.0',
                rating: '5',
                id: '8528852509',
                title: 'Super helpful app',
                content: 'Amazing bc I never have to worry about releases going poorly again!',
                fetchedAt: '2022-04-05T11:38:50.77282+01:00',
                submittedAt: '2022-04-03T16:54:44-07:00',
            },
            {
                author: '',
                version: '1.0.0',
                rating: '3',
                id: '8524205862',
                title: 'This is one of my favorite apps',
                content: 'I use it all the time and I tell all my friends about it. A+',
                fetchedAt: '2022-04-05T11:38:50.77282+01:00',
                submittedAt: '2022-04-02T11:17:22-07:00',
            },
        ],
        viewAllReviewsUrl: 'https://apps.apple.com/us/app/123456/reviews',
    },
    featureFlagTimeSeries: [
        {
            timestamp: '2022-11-01T10:00:00.491445Z',
            flagBefore: null,
            flagAfter: {
                id: 'roll_out_test',
                name: 'Roll out test',
                createdAt: '2022-10-14T16:16:10.56777Z',
                updatedAt: '2022-11-01T10:00:00.491445Z',
                enabledAt: null,
                minimumVersion: '1.1.0',
                rolloutPercentage: 0.5,
                status: 'enabled',
                isNewlyEnabledInThisVersion: true,
                isNewlyDisabledInThisVersion: false,
                isSpecificallyTargetingThisVersion: false,
                isSpecificallyNotTargetingThisVersion: false,
                didChangeInThisVersion: false,
                shouldHighlight: true,
                highlightTooltipText: 'This flag was enabled after this release was created.',
                isStale: false,
                staleTooltipText: '',
                isEstimatedEnabledAt: false,
                isReadOnly: false,
                rules: [
                    {
                        id: 'cf57f7c1-9874-4403-b858-b80021ea181b',
                        name: 'Roll out group',
                        isDefault: false,
                        audienceConditions: null,
                        conditions: [
                            {
                                attribute: 'userId',
                                negate: false,
                                predicate: 'matches',
                                values: [
                                    'd1b3b3b3-7b3e-4c9a-8622-7c1f19db0f8b',
                                    '44aee7a1-d5d6-46dd-85a0-f76d86c072c0',
                                    'd5aad47b-a4f5-41d2-ac00-ec1627aab439',
                                ],
                            },
                        ],
                        variations: [
                            {
                                key: '440c5e05-7bbe-4c9a-8622-7c1f19db0f8b',
                                name: 'On',
                                weight: 100,
                            },
                        ],
                    },
                    {
                        id: 'd78f171a-4cfe-4f42-810f-9699d4e16e47',
                        name: 'Version > 1.0.0',
                        isDefault: false,
                        audienceConditions: null,
                        conditions: [
                            {
                                attribute: 'appVersion',
                                negate: false,
                                predicate: 'greaterThan',
                                values: ['1.1.0'],
                            },
                        ],
                        variations: [
                            {
                                key: 'on',
                                name: 'On',
                                weight: 100,
                            },
                        ],
                    },
                    {
                        id: 'default',
                        name: 'Default',
                        isDefault: true,
                        audienceConditions: null,
                        conditions: null,
                        variations: [
                            {
                                key: 'off',
                                name: 'Off',
                                weight: 100,
                            },
                        ],
                    },
                ],
                owner: null,
                variations: [
                    {
                        key: '2nd_rollout',
                        name: '2nd rollout',
                        rolloutPercentage: 0.3,
                        variables: [
                            { type: 'double', key: 'target', value: '0.15' },
                            { type: 'string', key: 'user_type', value: 'demo' },
                        ],
                    },
                    {
                        key: 'on',
                        name: 'On',
                        rolloutPercentage: 0,
                        variables: [{ type: 'double', key: 'target', value: '100' }],
                    },
                    {
                        key: 'on',
                        name: 'Off',
                        rolloutPercentage: 0.7,
                        variables: [{ type: 'double', key: 'target', value: '0' }],
                    },
                ],
                url: '',
            },
            diff: [
                {
                    type: 'update',
                    path: ['rules/roll_out'],
                    from: 50,
                    to: 30,
                },
            ],
        },
        {
            timestamp: '2022-10-30T18:55:27.996195Z',
            flagBefore: null,
            flagAfter: {
                id: 'new_product_page',
                name: 'New product page',
                createdAt: '2022-09-14T18:52:17.592443Z',
                updatedAt: '2022-10-30T18:55:27.996195Z',
                enabledAt: null,
                minimumVersion: '1.1.0',
                rolloutPercentage: 1,
                status: 'enabled',
                isNewlyEnabledInThisVersion: false,
                isNewlyDisabledInThisVersion: false,
                isSpecificallyTargetingThisVersion: false,
                isSpecificallyNotTargetingThisVersion: false,
                shouldHighlight: false,
                highlightTooltipText: '',
                isStale: false,
                staleTooltipText: '',
                didChangeInThisVersion: false,
                isEstimatedEnabledAt: false,
                isReadOnly: false,
                rules: [
                    {
                        id: 'cf57f7c1-9874-4403-b858-b80021ea181b',
                        name: 'USA or Canada',
                        isDefault: false,
                        audienceConditions: null,
                        conditions: [
                            {
                                attribute: 'country',
                                negate: false,
                                predicate: 'matches',
                                values: ['US', 'CA'],
                            },
                        ],
                        variations: [
                            {
                                key: 'on',
                                name: 'On',
                                weight: 100,
                            },
                        ],
                    },
                    {
                        id: 'd78f171a-4cfe-4f42-810f-9699d4e16e47',
                        name: 'Version > 1.0.0',
                        isDefault: false,
                        audienceConditions: null,
                        conditions: [
                            {
                                attribute: 'appVersion',
                                negate: false,
                                predicate: 'greaterThan',
                                values: ['1.1.0'],
                            },
                        ],
                        variations: [
                            {
                                key: 'on',
                                name: 'On',
                                weight: 100,
                            },
                        ],
                    },
                ],
                owner: null,
                variations: [
                    {
                        key: 'special',
                        name: 'Special experimental variation',
                        rolloutPercentage: 0.25,
                        variables: null,
                    },
                    { key: 'on', name: 'On', rolloutPercentage: 0.25, variables: null },
                    { key: 'off', name: 'Off', rolloutPercentage: 0.5, variables: null },
                ],
                url: '',
            },
            diff: [
                {
                    type: 'update',
                    path: ['rules/new_product_page_rollout'],
                    from: 5,
                    to: 10,
                },
            ],
        },
    ],
    releaseAdoptions: {
        'sentry:xid': [
            {
                releaseId: '1.0.0',
                adoptionRate: 5,
                externalVersionString: '1.0.0',
            },
            {
                releaseId: '0.1.0',
                adoptionRate: 95,
                externalVersionString: '0.1.0',
            },
        ],
    },
    monitoringAutomations: [
        {
            id: 'AutoPromoteStablePhasedReleases',
            type: 'AutoPromoteStablePhasedReleases',
            triggeredAt: null,
            associatedMetrics: [
                {
                    id: 'stability@sessionStability',
                    status: 'healthy',
                },
                {
                    id: 'productAnalytics@eventsPerActiveUser:checkout success',
                    status: 'healthy',
                },
            ],
            associatedConditions: [
                {
                    type: 'adoption',
                    isConditionMet: false,
                },
            ],
            steps: [],
        },
        {
            id: 'AutoHaltUnstablePhasedReleases',
            type: 'AutoHaltUnstablePhasedReleases',
            triggeredAt: null,
            associatedMetrics: [
                {
                    id: 'stability@sessionStability',
                    status: 'healthy',
                },
                {
                    id: 'productAnalytics@eventsPerActiveUser:checkout success',
                    status: 'healthy',
                },
            ],
            associatedConditions: [
                {
                    type: 'adoption',
                    isConditionMet: false,
                },
            ],
            steps: [],
        },
        {
            id: 'AutoPrepareRollbackRelease',
            type: 'AutoPrepareRollbackRelease',
            triggeredAt: null,
            associatedMetrics: [
                {
                    id: 'stability@sessionStability',
                    status: 'healthy',
                },
            ],
            associatedConditions: [
                {
                    type: 'adoption',
                    isConditionMet: true,
                },
            ],
            steps: [
                {
                    status: AutomationStatus.Success,
                    description: 'Re-signing of 1.0.0 build',
                },
                {
                    status: AutomationStatus.Success,
                    description: 'Rollback 1.0.1 submitted for review by Runway',
                },
                {
                    status: AutomationStatus.Success,
                    description: '1.0.1 approved for release',
                },
                {
                    status: AutomationStatus.Pending,
                    description: 'Deploy rollback',
                },
            ],
        },
    ],
    monitoringStatistics: [
        {
            integrationId: 'sentry',
            integrationInstallationId: 'sentry:xid',
            type: 'sessionStability',
            currentValue: 99.5095853767276,
            monitoringMetricId: 'stability@sessionStability',
            eventName: null,
            monitoringMetricStatus: 'healthy',
            recentDataPoints: null,
            pastDataPoints: [
                {
                    timestamp: '2022-10-28T00:00:00Z',
                    value: 99.54022988505747,
                },
                {
                    timestamp: '2022-10-29T00:00:00Z',
                    value: 99.57081545064378,
                },
                {
                    timestamp: '2022-10-30T00:00:00Z',
                    value: 99.60060353243986,
                },
                {
                    timestamp: '2022-10-31T00:00:00Z',
                    value: 99.57253539941223,
                },
                {
                    timestamp: '2022-11-01T00:00:00Z',
                    value: 99.52261877699478,
                },
                {
                    timestamp: '2022-11-02T00:00:00Z',
                    value: 99.51562968141417,
                },
                {
                    timestamp: '2022-11-03T00:00:00Z',
                    value: 99.5095853767276,
                },
            ],
        },
        {
            integrationId: 'sentry',
            integrationInstallationId: 'sentry:xid',
            type: 'userStability',
            currentValue: 98.87982466820894,
            monitoringMetricId: null,
            eventName: null,
            monitoringMetricStatus: null,
            recentDataPoints: null,
            pastDataPoints: [
                {
                    timestamp: '2022-10-28T00:00:00Z',
                    value: 99.04942965779468,
                },
                {
                    timestamp: '2022-10-29T00:00:00Z',
                    value: 99.10543130990416,
                },
                {
                    timestamp: '2022-10-30T00:00:00Z',
                    value: 99.12955465587045,
                },
                {
                    timestamp: '2022-10-31T00:00:00Z',
                    value: 99.03005247257116,
                },
                {
                    timestamp: '2022-11-01T00:00:00Z',
                    value: 98.9196675900277,
                },
                {
                    timestamp: '2022-11-02T00:00:00Z',
                    value: 98.883298564241,
                },
                {
                    timestamp: '2022-11-03T00:00:00Z',
                    value: 98.87982466820894,
                },
            ],
        },
        {
            integrationId: isIosPlatform(platform) ? 'apple' : 'google',
            integrationInstallationId: isIosPlatform(platform) ? 'apple:xid' : 'google:xid',
            type: 'avgUserRating',
            currentValue: 4.5,
            monitoringMetricId: 'userReviews@avgUserRating',
            eventName: null,
            monitoringMetricStatus: null,
            recentDataPoints: null,
            pastDataPoints: [
                {
                    timestamp: '2022-11-01T00:00:00Z',
                    value: 4.6,
                },
                {
                    timestamp: '2022-11-02T00:00:00Z',
                    value: 4.92,
                },
                {
                    timestamp: '2022-11-03T00:00:00Z',
                    value: 4.08,
                },
                {
                    timestamp: '2022-11-04T00:00:00Z',
                    value: 4.1,
                },
                {
                    timestamp: '2022-11-05T00:00:00Z',
                    value: 4.29,
                },
                {
                    timestamp: '2022-11-06T00:00:00Z',
                    value: 5.01,
                },
                {
                    timestamp: '2022-11-07T00:00:00Z',
                    value: 4.76,
                },
                {
                    timestamp: '2022-11-08T00:00:00Z',
                    value: 4.75,
                },
                {
                    timestamp: '2022-11-09T00:00:00Z',
                    value: 4.18,
                },
                {
                    timestamp: '2022-11-10T00:00:00Z',
                    value: 4.3,
                },
            ],
        },
        {
            integrationId: 'amplitude',
            integrationInstallationId: 'amplitude:xid',
            type: 'eventsPerActiveUser',
            currentValue: 0.7968287526427061,
            monitoringMetricId: null,
            eventName: 'invite friend',
            monitoringMetricStatus: null,
            recentDataPoints: [],
            pastDataPoints: [
                {
                    timestamp: '2022-10-28T00:00:00Z',
                    value: 0.6285979572887651,
                },
                {
                    timestamp: '2022-10-29T00:00:00Z',
                    value: 0.7627702914446882,
                },
                {
                    timestamp: '2022-10-30T00:00:00Z',
                    value: 0.8024805627545354,
                },
                {
                    timestamp: '2022-10-31T00:00:00Z',
                    value: 0.8046209386281589,
                },
                {
                    timestamp: '2022-11-01T00:00:00Z',
                    value: 0.8028822667318026,
                },
                {
                    timestamp: '2022-11-02T00:00:00Z',
                    value: 0.79706132879046,
                },
                {
                    timestamp: '2022-11-03T00:00:00Z',
                    value: 0.7968287526427061,
                },
            ],
        },
        {
            integrationId: 'amplitude',
            integrationInstallationId: 'amplitude:xid',
            type: 'eventsPerSession',
            currentValue: 0.6128355142160393,
            monitoringMetricId: null,
            eventName: 'invite friend',
            monitoringMetricStatus: null,
            recentDataPoints: [],
            pastDataPoints: [
                {
                    timestamp: '2022-10-28T00:00:00Z',
                    value: 0.48580215274218347,
                },
                {
                    timestamp: '2022-10-29T00:00:00Z',
                    value: 0.5885220848462754,
                },
                {
                    timestamp: '2022-10-30T00:00:00Z',
                    value: 0.615703621106557,
                },
                {
                    timestamp: '2022-10-31T00:00:00Z',
                    value: 0.6169735962049264,
                },
                {
                    timestamp: '2022-11-01T00:00:00Z',
                    value: 0.6159993063493848,
                },
                {
                    timestamp: '2022-11-02T00:00:00Z',
                    value: 0.6122490968951714,
                },
                {
                    timestamp: '2022-11-03T00:00:00Z',
                    value: 0.6128355142160393,
                },
            ],
        },
        {
            integrationId: 'amplitude',
            integrationInstallationId: 'amplitude:xid',
            type: 'eventsPerActiveUser',
            currentValue: 2.7014799154334037,
            monitoringMetricId: null,
            eventName: 'view favorites',
            monitoringMetricStatus: null,
            recentDataPoints: [],
            pastDataPoints: [
                {
                    timestamp: '2022-10-28T00:00:00Z',
                    value: 2.5153203342618387,
                },
                {
                    timestamp: '2022-10-29T00:00:00Z',
                    value: 2.610466938263867,
                },
                {
                    timestamp: '2022-10-30T00:00:00Z',
                    value: 2.657719363198815,
                },
                {
                    timestamp: '2022-10-31T00:00:00Z',
                    value: 2.6859205776173285,
                },
                {
                    timestamp: '2022-11-01T00:00:00Z',
                    value: 2.701636541279922,
                },
                {
                    timestamp: '2022-11-02T00:00:00Z',
                    value: 2.7028321976149914,
                },
                {
                    timestamp: '2022-11-03T00:00:00Z',
                    value: 2.7014799154334037,
                },
            ],
        },
        {
            integrationId: 'amplitude',
            integrationInstallationId: 'amplitude:xid',
            type: 'eventsPerSession',
            currentValue: 2.0776896260685995,
            monitoringMetricId: null,
            eventName: 'view favorites',
            monitoringMetricStatus: null,
            recentDataPoints: [],
            pastDataPoints: [
                {
                    timestamp: '2022-10-28T00:00:00Z',
                    value: 1.9439261916965658,
                },
                {
                    timestamp: '2022-10-29T00:00:00Z',
                    value: 2.0141285812528658,
                },
                {
                    timestamp: '2022-10-30T00:00:00Z',
                    value: 2.039136537076548,
                },
                {
                    timestamp: '2022-10-31T00:00:00Z',
                    value: 2.059531387188017,
                },
                {
                    timestamp: '2022-11-01T00:00:00Z',
                    value: 2.072789877662723,
                },
                {
                    timestamp: '2022-11-02T00:00:00Z',
                    value: 2.076134561138648,
                },
                {
                    timestamp: '2022-11-03T00:00:00Z',
                    value: 2.0776896260685995,
                },
            ],
        },
        {
            integrationId: 'amplitude',
            integrationInstallationId: 'amplitude:xid',
            type: 'eventsPerActiveUser',
            currentValue: 0.31553911205074,
            monitoringMetricId: null,
            eventName: 'save favorite',
            monitoringMetricStatus: null,
            recentDataPoints: [],
            pastDataPoints: [
                {
                    timestamp: '2022-10-28T00:00:00Z',
                    value: 0.2646239554317549,
                },
                {
                    timestamp: '2022-10-29T00:00:00Z',
                    value: 0.26888122845502976,
                },
                {
                    timestamp: '2022-10-30T00:00:00Z',
                    value: 0.2850796001480933,
                },
                {
                    timestamp: '2022-10-31T00:00:00Z',
                    value: 0.30685920577617326,
                },
                {
                    timestamp: '2022-11-01T00:00:00Z',
                    value: 0.3201025891548608,
                },
                {
                    timestamp: '2022-11-02T00:00:00Z',
                    value: 0.31590715502555367,
                },
                {
                    timestamp: '2022-11-03T00:00:00Z',
                    value: 0.31553911205074,
                },
            ],
        },
        {
            integrationId: 'amplitude',
            integrationInstallationId: 'amplitude:xid',
            type: 'eventsPerSession',
            currentValue: 0.2426789612542953,
            monitoringMetricId: null,
            eventName: 'save favorite',
            monitoringMetricStatus: null,
            recentDataPoints: [],
            pastDataPoints: [
                {
                    timestamp: '2022-10-28T00:00:00Z',
                    value: 0.2045105074320861,
                },
                {
                    timestamp: '2022-10-29T00:00:00Z',
                    value: 0.20745766179051126,
                },
                {
                    timestamp: '2022-10-30T00:00:00Z',
                    value: 0.2187274686283962,
                },
                {
                    timestamp: '2022-10-31T00:00:00Z',
                    value: 0.2352959246115342,
                },
                {
                    timestamp: '2022-11-01T00:00:00Z',
                    value: 0.24559388225459958,
                },
                {
                    timestamp: '2022-11-02T00:00:00Z',
                    value: 0.2426587056489411,
                },
                {
                    timestamp: '2022-11-03T00:00:00Z',
                    value: 0.2426789612542953,
                },
            ],
        },
        {
            integrationId: 'amplitude',
            integrationInstallationId: 'amplitude:xid',
            type: 'eventsPerActiveUser',
            currentValue: 0.5943974630021142,
            monitoringMetricId: null,
            eventName: 'add to cart',
            monitoringMetricStatus: null,
            recentDataPoints: [],
            pastDataPoints: [
                {
                    timestamp: '2022-10-28T00:00:00Z',
                    value: 0.48653667595171773,
                },
                {
                    timestamp: '2022-10-29T00:00:00Z',
                    value: 0.5907239109996866,
                },
                {
                    timestamp: '2022-10-30T00:00:00Z',
                    value: 0.6142169566827101,
                },
                {
                    timestamp: '2022-10-31T00:00:00Z',
                    value: 0.6023104693140794,
                },
                {
                    timestamp: '2022-11-01T00:00:00Z',
                    value: 0.6001465559355154,
                },
                {
                    timestamp: '2022-11-02T00:00:00Z',
                    value: 0.59412265758092,
                },
                {
                    timestamp: '2022-11-03T00:00:00Z',
                    value: 0.5943974630021142,
                },
            ],
        },
        {
            integrationId: 'amplitude',
            integrationInstallationId: 'amplitude:xid',
            type: 'eventsPerSession',
            currentValue: 0.45714700138455694,
            monitoringMetricId: null,
            eventName: 'add to cart',
            monitoringMetricStatus: null,
            recentDataPoints: [],
            pastDataPoints: [
                {
                    timestamp: '2022-10-28T00:00:00Z',
                    value: 0.3760123013839057,
                },
                {
                    timestamp: '2022-10-29T00:00:00Z',
                    value: 0.4557781963579414,
                },
                {
                    timestamp: '2022-10-30T00:00:00Z',
                    value: 0.4712582733175445,
                },
                {
                    timestamp: '2022-10-31T00:00:00Z',
                    value: 0.4618443772022161,
                },
                {
                    timestamp: '2022-11-01T00:00:00Z',
                    value: 0.4604533908428471,
                },
                {
                    timestamp: '2022-11-02T00:00:00Z',
                    value: 0.4563652098149954,
                },
                {
                    timestamp: '2022-11-03T00:00:00Z',
                    value: 0.45714700138455694,
                },
            ],
        },
        {
            integrationId: 'amplitude',
            integrationInstallationId: 'amplitude:xid',
            type: 'eventsPerActiveUser',
            currentValue: 0.7674418604651163,
            monitoringMetricId: 'productAnalytics@eventsPerActiveUser:checkout success',
            eventName: 'checkout success',
            monitoringMetricStatus: null,
            recentDataPoints: [],
            pastDataPoints: [
                {
                    timestamp: '2022-10-28T00:00:00Z',
                    value: 0.6155988857938719,
                },
                {
                    timestamp: '2022-10-29T00:00:00Z',
                    value: 0.7527420871200251,
                },
                {
                    timestamp: '2022-10-30T00:00:00Z',
                    value: 0.7841540170307294,
                },
                {
                    timestamp: '2022-10-31T00:00:00Z',
                    value: 0.776173285198556,
                },
                {
                    timestamp: '2022-11-01T00:00:00Z',
                    value: 0.776257938446507,
                },
                {
                    timestamp: '2022-11-02T00:00:00Z',
                    value: 0.7674616695059625,
                },
                {
                    timestamp: '2022-11-03T00:00:00Z',
                    value: 0.7674418604651163,
                },
            ],
        },
        {
            integrationId: 'amplitude',
            integrationInstallationId: 'amplitude:xid',
            type: 'eventsPerSession',
            currentValue: 0.5902342575230096,
            monitoringMetricId: 'productAnalytics@eventsPerSession:checkout success',
            eventName: 'checkout success',
            monitoringMetricStatus: 'healthy',
            recentDataPoints: [],
            pastDataPoints: [
                {
                    timestamp: '2022-10-28T00:00:00Z',
                    value: 0.47575602255253713,
                },
                {
                    timestamp: '2022-10-29T00:00:00Z',
                    value: 0.5807847361547878,
                },
                {
                    timestamp: '2022-10-30T00:00:00Z',
                    value: 0.6016425695518742,
                },
                {
                    timestamp: '2022-10-31T00:00:00Z',
                    value: 0.5951602798997631,
                },
                {
                    timestamp: '2022-11-01T00:00:00Z',
                    value: 0.5955721921443092,
                },
                {
                    timestamp: '2022-11-02T00:00:00Z',
                    value: 0.5895126222843167,
                },
                {
                    timestamp: '2022-11-03T00:00:00Z',
                    value: 0.5902342575230096,
                },
            ],
        },
        {
            integrationId: 'amplitude',
            integrationInstallationId: 'amplitude:xid',
            type: 'eventsPerActiveUser',
            currentValue: 4.0127906976744185,
            monitoringMetricId: 'productAnalytics@eventsPerActiveUser:view product page',
            eventName: 'view product page',
            monitoringMetricStatus: 'healthy',
            recentDataPoints: [],
            pastDataPoints: [
                {
                    timestamp: '2022-10-28T00:00:00Z',
                    value: 4.15506035283194,
                },
                {
                    timestamp: '2022-10-29T00:00:00Z',
                    value: 4.085866499529928,
                },
                {
                    timestamp: '2022-10-30T00:00:00Z',
                    value: 4.037578674564976,
                },
                {
                    timestamp: '2022-10-31T00:00:00Z',
                    value: 4.0551624548736465,
                },
                {
                    timestamp: '2022-11-01T00:00:00Z',
                    value: 4.027112848070347,
                },
                {
                    timestamp: '2022-11-02T00:00:00Z',
                    value: 4.016077512776832,
                },
                {
                    timestamp: '2022-11-03T00:00:00Z',
                    value: 4.0127906976744185,
                },
            ],
        },
        {
            integrationId: 'amplitude',
            integrationInstallationId: 'amplitude:xid',
            type: 'eventsPerSession',
            currentValue: 3.086209731381676,
            monitoringMetricId: null,
            eventName: 'view product page',
            monitoringMetricStatus: null,
            recentDataPoints: [],
            pastDataPoints: [
                {
                    timestamp: '2022-10-28T00:00:00Z',
                    value: 3.2111737570476677,
                },
                {
                    timestamp: '2022-10-29T00:00:00Z',
                    value: 3.152486007487979,
                },
                {
                    timestamp: '2022-10-30T00:00:00Z',
                    value: 3.0978342975674997,
                },
                {
                    timestamp: '2022-10-31T00:00:00Z',
                    value: 3.1094494846781666,
                },
                {
                    timestamp: '2022-11-01T00:00:00Z',
                    value: 3.0897415770557672,
                },
                {
                    timestamp: '2022-11-02T00:00:00Z',
                    value: 3.0848816037655578,
                },
                {
                    timestamp: '2022-11-03T00:00:00Z',
                    value: 3.086209731381676,
                },
            ],
        },
    ],
    checklistItems: [],
    automationRuns: [
        {
            name: 'Release stable phased releases to 100% of users',
            id: 'AutoPromoteStablePhasedReleases',
            stepConfigId: null,
            type: AutomationType.AutoPromoteStablePhasedReleases,

            description: 'Release stable phased releases to 100% of users',
            status: AutomationStatus.Active,
            isLifecycle: false,
            scheduledAt: 'a time',
            autoScheduledFromCadence: false,
            statusMessage: 'Status message: Inactive for rollback releases.',
            statusMessageLinkUrl: null,
            isPartOfSequence: false,
            sequenceNumber: 1,
            hasEditableSettings: null,
            relevantPRs: [],
        },
        {
            name: 'Prepare rollback releases',
            id: 'AutoPrepareRollbackRelease',
            stepConfigId: null,
            type: AutomationType.AutoPrepareRollbackRelease,

            description:
                'Runway will re-sign, upload and (optionally) submit the previous stable build to have ready in case your latest release needs to be pulled back.',
            status: AutomationStatus.Active,
            isLifecycle: false,
            scheduledAt: 'a time',
            autoScheduledFromCadence: false,
            statusMessage: '',
            statusMessageLinkUrl: null,
            isPartOfSequence: false,
            sequenceNumber: 1,
            hasEditableSettings: null,
            relevantPRs: [],
        },
        {
            name: 'Custom phased / staged rollout',
            id: 'AutoStagedRollout',
            stepConfigId: null,
            type: AutomationType.AutoStagedRollout,

            description:
                'iOS: Customize phased rollouts with blackout dates and times – Runway will pause and resume phased rollouts to avoid phased rollout percentage increments on the blackout dates and times you specify.',
            status: AutomationStatus.Active,
            isLifecycle: false,
            scheduledAt: 'a time',
            autoScheduledFromCadence: false,
            statusMessage: 'Status message: Active for staged rollouts.',
            statusMessageLinkUrl: null,
            isPartOfSequence: false,
            sequenceNumber: 1,
            hasEditableSettings: null,
            relevantPRs: [],
        },
        {
            name: 'Halt unstable phased releases',
            id: 'AutoHaltUnstablePhasedReleases',
            stepConfigId: null,
            type: AutomationType.AutoHaltUnstablePhasedReleases,
            description:
                'Runway will pause your phased release if the session stability for the update decreases by a specified percentage relative to your average. Your current average session stability is 99.98%.',
            status: AutomationStatus.Active,
            isLifecycle: false,
            scheduledAt: 'a time',
            autoScheduledFromCadence: false,
            statusMessage: 'Status message: Active for halt unstable phased releases.',
            statusMessageLinkUrl: null,
            isPartOfSequence: false,
            sequenceNumber: 1,
            hasEditableSettings: null,
            relevantPRs: [],
        },
    ],
});
